import { upgradeCanvasDocumentV3, type CanvasDocumentV3 as CanvasDocument, type CanvasViewV3 as CanvasView, type SaveCanvasV3Request as SaveCanvasRequest } from "@seedance/contracts";
import { ApiClientError } from "../api/client.js";
import type { CanvasDraft, CanvasDraftStore } from "./draft-store.js";

export type SaveState =
  | { status: "loading" }
  | { status: "saved"; revision: string }
  | { status: "saving"; revision: string }
  | { status: "offline_draft"; revision: string }
  | { status: "save_failed"; revision: string; requestId?: string }
  | { status: "conflict"; revision: string; local: CanvasDocument; upgradeRequired?:boolean };

export interface CanvasRecoveryState {
  durable: boolean;
  localError: boolean;
}

export interface CanvasSessionControllerOptions {
  ownerId: string;
  projectId: string;
  initialRevision: string;
  initialDocument: CanvasDocument;
  saveCanvas(projectId: string, input: SaveCanvasRequest): Promise<CanvasView>;
  drafts: CanvasDraftStore;
  onState?(state: SaveState): void;
  onDocument?(document: CanvasDocument): void;
  onRecoveryState?(state: CanvasRecoveryState): void;
  now?(): number;
}

export interface CanvasSessionController {
  change(document: CanvasDocument): void;
  retry(): void;
  getMutationVersion(): number;
  recoverDraft(draft: CanvasDraft, expectedMutationVersion: number): Promise<void>;
  replaceWithServer(view: CanvasView): Promise<void>;
  applyServerCommand(
    view: CanvasView,
    merge: (current: CanvasDocument, server: CanvasDocument) => CanvasDocument
  ): void;
  runServerCommand<Result extends { canvas: CanvasView }>(
    command: (revision: string) => Promise<Result>,
    merge: (current: CanvasDocument, result: Result) => CanvasDocument
  ): Promise<Result | undefined>;
  runWithSavedRevision<Result>(
    command: (revision: string) => Promise<Result>
  ): Promise<Result | undefined>;
  getDocument(): CanvasDocument;
  getState(): SaveState;
  getRecoveryState(): CanvasRecoveryState;
  dispose(): void;
}

const idleDelay = 750;
const continuousSaveCeiling = 10_000;

export function createCanvasSessionController(
  options: CanvasSessionControllerOptions
): CanvasSessionController {
  const now = options.now ?? Date.now;
  let document = options.initialDocument;
  let revision = options.initialRevision;
  let state: SaveState = { status: "saved", revision };
  let idleTimer: ReturnType<typeof setTimeout> | undefined;
  let ceilingTimer: ReturnType<typeof setTimeout> | undefined;
  let firstChangeAt: number | undefined;
  let dirty = false;
  let saving = false;
  let saveCompletion: Promise<void> = Promise.resolve();
  let serverCommandRunning = false;
  let conflict = false;
  let upgradeRequired = false;
  let conflictBaseRevision = revision;
  let disposed = false;
  let mutationVersion = 0;
  let draftOperations: Promise<void> = Promise.resolve();
  const writerId = crypto.randomUUID();
  let persistedDraft: CanvasDraft | undefined;
  let branch = 0;
  let persistedBranch = 0;
  let localVersion = -1;
  let remoteVersion = 0;
  let localError = false;
  const recoveryState = (): CanvasRecoveryState => ({
    durable: remoteVersion === mutationVersion || localVersion === mutationVersion,
    localError
  });
  const publishRecovery = () => {
    if (!disposed) options.onRecoveryState?.(recoveryState());
  };

  const setState = (next: SaveState) => {
    state = next;
    options.onState?.(state);
  };
  const clearTimers = () => {
    if (idleTimer) clearTimeout(idleTimer);
    if (ceilingTimer) clearTimeout(ceilingTimer);
    idleTimer = undefined;
    ceilingTimer = undefined;
    firstChangeAt = undefined;
  };

  const draftFor = (draftDocument: CanvasDocument, baseRevision: string): CanvasDraft => ({
    ownerId: options.ownerId,
    projectId: options.projectId,
    baseRevision: conflict ? conflictBaseRevision : baseRevision,
    document: draftDocument,
    savedAt: new Date().toISOString(),
    writerId,
    draftId: `${mutationVersion}`
  });

  // Serialize IndexedDB operations. In particular, a server response for an
  // older mutation must not delete a draft that was queued for a newer one.
  const persistDraft = (draft: CanvasDraft) => {
    const version = mutationVersion;
    const draftBranch = branch;
    const write = draftOperations.then(async () => {
      try {
        await options.drafts.put(draft);
        const previous = persistedDraft;
        const previousBranch = persistedBranch;
        persistedDraft = draft;
        persistedBranch = draftBranch;
        localVersion = version;
        if (version === mutationVersion) localError = false;
        publishRecovery();
        // Delete only this controller's older snapshot after its successor commits.
        if (previous && previousBranch === draftBranch && previous.draftId !== draft.draftId) {
          await options.drafts.remove(previous).catch(() => undefined);
        }
        return true;
      } catch {
        if (version === mutationVersion) localError = true;
        publishRecovery();
        return false;
      }
    });
    draftOperations = write.then(() => undefined);
    return write;
  };

  const removeDraft = (acknowledgedVersion = mutationVersion) => {
    const acknowledgedBranch = branch;
    const operation = draftOperations.then(async () => {
      const expected = persistedDraft;
      if (expected && persistedBranch === acknowledgedBranch && Number(expected.draftId) <= acknowledgedVersion) {
        await options.drafts.remove(expected);
        if (persistedDraft === expected) persistedDraft = undefined;
      }
    });
    // Cleanup failure must not turn a confirmed server save into a failed save.
    draftOperations = operation.catch(() => undefined);
    return draftOperations;
  };

  const scheduleSave = () => {
    if (disposed || conflict || saving || serverCommandRunning || !dirty) return;
    if (idleTimer) clearTimeout(idleTimer);
    idleTimer = setTimeout(() => void save(), idleDelay);
    if (firstChangeAt === undefined) {
      firstChangeAt = now();
      ceilingTimer = setTimeout(() => void save(), continuousSaveCeiling);
    }
  };

  const save = (): Promise<void> => {
    if (saving) return saveCompletion;
    if (disposed || conflict || !dirty) return Promise.resolve();
    saving = true;
    clearTimers();
    const savedDocument = document;
    const savedRevision = revision;
    const savedMutationVersion = mutationVersion;
    dirty = false;
    setState({ status: "saving", revision: savedRevision });
    const operation = (async () => {
      try {
        const result = await options.saveCanvas(options.projectId, {
          revision: savedRevision,
          document: savedDocument
        });
        revision = result.revision;
        remoteVersion = savedMutationVersion;
        publishRecovery();
        if (mutationVersion === savedMutationVersion) {
          await removeDraft(savedMutationVersion);
        } else {
          await persistDraft(draftFor(document, revision));
        }
        if (!disposed) setState({ status: mutationVersion === savedMutationVersion ? "saved" : "saving", revision });
      } catch (error) {
        if (error instanceof ApiClientError && error.status === 409) {
          conflict = true;
          upgradeRequired = error.code === "CANVAS_CLIENT_UPGRADE_REQUIRED";
          conflictBaseRevision = savedRevision;
          dirty = false;
          await persistDraft(draftFor(document, revision));
          if (!disposed) {
            setState({ status: "conflict", revision, local: document, ...(upgradeRequired ? {upgradeRequired:true} : {}) });
          }
        } else {
          await persistDraft(draftFor(document, revision));
          if (!disposed) {
            if (dirty) {
              setState({ status: "saving", revision });
            } else if (error instanceof TypeError && recoveryState().durable) {
              setState({ status: "offline_draft", revision: savedRevision });
            } else {
              setState({
                status: "save_failed",
                revision: savedRevision,
                ...(error instanceof ApiClientError ? { requestId: error.requestId } : {})
              });
            }
          }
        }
      } finally {
        saving = false;
        if (dirty && !conflict) scheduleSave();
      }
    })();
    saveCompletion = operation;
    return operation;
  };

  return {
    change(nextDocument) {
      if (disposed) return;
      mutationVersion += 1;
      document = nextDocument;
      dirty = !conflict;
      setState(conflict ? { status: "conflict", revision, local: document, ...(upgradeRequired ? {upgradeRequired:true} : {}) } : { status: "saving", revision });
      publishRecovery();
      void persistDraft(draftFor(document, revision));
      options.onDocument?.(document);
      scheduleSave();
    },
    retry() {
      if (disposed) return;
      // A local-storage warning can outlive a successful remote acknowledgement.
      // Repair that persistence independently without creating another server write.
      void persistDraft(draftFor(document, revision));
      if (conflict || (state.status === "saved" && !dirty)) return;
      dirty = true;
      // External retries share the command gate; its internal pre-flush still
      // calls save() directly, and command completion schedules pending edits.
      if (!serverCommandRunning) void save();
    },
    getMutationVersion() {
      return mutationVersion;
    },
    async recoverDraft(draft, expectedMutationVersion) {
      if (disposed || saving || serverCommandRunning || mutationVersion !== expectedMutationVersion) return;
      // Validate and detach before touching active state; migration must retain its source branch.
      const recovered = upgradeCanvasDocumentV3(draft.document).document;
      upgradeRequired = false;
      clearTimers();
      branch += 1;
      dirty = false;
      mutationVersion += 1;
      document = recovered;
      const recoveredVersion = mutationVersion;
      localError = false;
      publishRecovery();
      options.onDocument?.(document);
      if (draft.baseRevision === revision) {
        conflict = false;
        setState({ status: "saving", revision });
      } else {
        conflict = true;
        conflictBaseRevision = draft.baseRevision;
        setState({ status: "conflict", revision, local: document });
      }
      // Cached choices may have been superseded in another active tab. Establish
      // our own exact successor before claiming this recovered content is durable.
      const persisted = await persistDraft(draftFor(document, draft.baseRevision));
      if (!disposed && mutationVersion === recoveredVersion && !conflict) {
        setState({ status: persisted ? "offline_draft" : "save_failed", revision });
      }
    },
    async replaceWithServer(view) {
      if (disposed) return;
      clearTimers();
      branch += 1;
      conflict = false;
      upgradeRequired = false;
      dirty = false;
      mutationVersion += 1;
      document = view.document;
      revision = view.revision;
      remoteVersion = mutationVersion;
      publishRecovery();
      // Keep the abandoned local snapshot available in recovery choices.
      options.onDocument?.(document);
      setState({ status: "saved", revision });
    },
    applyServerCommand(view, merge) {
      if (disposed || conflict) return;
      clearTimers();
      revision = view.revision;
      mutationVersion += 1;
      document = merge(document, view.document);
      dirty = JSON.stringify(document) !== JSON.stringify(view.document);
      options.onDocument?.(document);
      if (!dirty) remoteVersion = mutationVersion;
      publishRecovery();
      setState({ status: dirty ? "saving" : "saved", revision });
      if (dirty) {
        void persistDraft(draftFor(document, revision));
        scheduleSave();
      } else {
        void removeDraft();
      }
    },
    async runServerCommand(command, merge) {
      if (disposed || conflict || serverCommandRunning) return undefined;
      serverCommandRunning = true;
      clearTimers();
      try {
        while (!disposed && !conflict && (saving || dirty)) {
          await (saving ? saveCompletion : save());
          if (state.status !== "saved" && !saving && !dirty) return undefined;
        }
        if (disposed || conflict || state.status !== "saved") return undefined;
        const result = await command(revision);
        if (disposed || conflict) return undefined;
        revision = result.canvas.revision;
        mutationVersion += 1;
        document = merge(document, result);
        dirty = JSON.stringify(document) !== JSON.stringify(result.canvas.document);
        if (!dirty) remoteVersion = mutationVersion;
        publishRecovery();
        options.onDocument?.(document);
        if (dirty) {
          setState({ status: "saving", revision });
          void persistDraft(draftFor(document, revision));
        } else {
          setState({ status: "saved", revision });
          void removeDraft();
        }
        return result;
      } finally {
        serverCommandRunning = false;
        if (dirty && !conflict) scheduleSave();
      }
    },
    async runWithSavedRevision(command) {
      if (disposed || conflict || serverCommandRunning) return undefined;
      serverCommandRunning = true;
      clearTimers();
      try {
        while (!disposed && !conflict && (saving || dirty)) {
          await (saving ? saveCompletion : save());
          if (state.status !== "saved" && !saving && !dirty) return undefined;
        }
        if (disposed || conflict || state.status !== "saved") return undefined;
        return await command(revision);
      } finally {
        serverCommandRunning = false;
        if (dirty && !conflict) scheduleSave();
      }
    },
    getDocument() {
      return document;
    },
    getState() {
      return state;
    },
    getRecoveryState() {
      return recoveryState();
    },
    dispose() {
      disposed = true;
      clearTimers();
    }
  };
}
